import React, { useState, useEffect } from "react";
import { Card, Button, Form, Alert, Spinner, Badge } from "react-bootstrap";
import axios from "axios";
import { useLanguage } from "../i18n/LanguageContext";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

interface SetupFor2FAProps {
  setupToken: string;
  userId: number;
  onComplete: () => void; // called after setup — user must log in again
  onBack: () => void;
}

const SetupFor2FA: React.FC<SetupFor2FAProps> = ({
  setupToken,
  userId,
  onComplete,
  onBack,
}) => {
  const { t, dir } = useLanguage();
  const [step, setStep] = useState<"intro" | "qr" | "verify" | "done">("intro");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verifyCode, setVerifyCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const authHeader = { Authorization: `Bearer ${setupToken}` };

  const handleSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(
        `${API_BASE_URL}/2fa/setup`,
        {},
        { headers: authHeader },
      );
      setQrCode(r.data.qrCode);
      setSecret(r.data.secret);
      setStep("qr");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to start setup");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(
        `${API_BASE_URL}/2fa/verify-setup`,
        { token: verifyCode },
        { headers: authHeader },
      );
      setBackupCodes(r.data.backupCodes || []);
      setStep("done");
    } catch (err: any) {
      setError(err.response?.data?.error || t("invalidCode"));
      setVerifyCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      className="shadow border-0"
      style={{ borderRadius: "16px" }}
      dir={dir}
    >
      <Card.Body className="p-4">
        <div className="text-center mb-4">
          <div style={{ fontSize: 40 }}>🔐</div>
          <h5 className="mb-1" style={{ fontWeight: 600 }}>
            {t("setupTwoFA")}
          </h5>
          <p className="text-muted small">{t("authenticatorSetupRequired")}</p>
        </div>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError("")}
            className="py-2 small"
          >
            {error}
          </Alert>
        )}

        {step === "intro" && (
          <>
            <p className="text-muted small text-center mb-4">
              {t("authenticatorSetupDesc")}
            </p>
            <div className="d-grid gap-2">
              <Button
                variant="primary"
                size="lg"
                onClick={handleSetup}
                disabled={loading}
                style={{ borderRadius: "8px", fontWeight: 600 }}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  t("setupTwoFA")
                )}
              </Button>
              <Button variant="link" className="text-muted" onClick={onBack}>
                ← {t("back")}
              </Button>
            </div>
          </>
        )}

        {step === "qr" && (
          <>
            <p className="text-center small text-muted mb-3">
              {t("scanQRCode")}
            </p>
            {qrCode && (
              <div className="text-center mb-3">
                <img
                  src={qrCode}
                  alt="QR Code"
                  style={{
                    width: 180,
                    height: 180,
                    border: "1px solid #dee2e6",
                    borderRadius: 8,
                  }}
                />
              </div>
            )}
            <div className="text-center mb-3">
              <small className="text-muted d-block mb-1">
                {t("or")} manual key:
              </small>
              <code
                style={{
                  fontSize: 13,
                  letterSpacing: "0.1em",
                  background: "#f8f9fa",
                  padding: "4px 8px",
                  borderRadius: 4,
                }}
              >
                {secret}
              </code>
            </div>
            <Form onSubmit={handleVerify}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">
                  {t("enterCode")}
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="000000"
                  value={verifyCode}
                  onChange={(e) =>
                    setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  maxLength={6}
                  autoFocus
                  style={{
                    fontSize: 24,
                    textAlign: "center",
                    letterSpacing: "0.3em",
                    padding: "12px",
                    borderRadius: 8,
                    fontWeight: "bold",
                  }}
                />
              </Form.Group>
              <div className="d-grid">
                <Button
                  variant="primary"
                  type="submit"
                  size="lg"
                  disabled={loading || verifyCode.length < 6}
                  style={{ borderRadius: 8, fontWeight: 600 }}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    t("submit")
                  )}
                </Button>
              </div>
            </Form>
          </>
        )}

        {step === "done" && (
          <>
            <Alert variant="success" className="text-center">
              <div style={{ fontSize: 32 }}>✅</div>
              <strong>{t("twoFAEnabled")}</strong>
            </Alert>
            {backupCodes.length > 0 && (
              <div className="mb-3">
                <p className="small fw-semibold mb-2">
                  {t("backupCode")} — {t("authenticatorSaveBackupCodes")}
                </p>
                <div
                  style={{
                    background: "#f8f9fa",
                    borderRadius: 8,
                    padding: "12px",
                    fontFamily: "monospace",
                    fontSize: 13,
                  }}
                >
                  {backupCodes.map((code, i) => (
                    <div
                      key={i}
                      className="d-flex align-items-center gap-2 mb-1"
                    >
                      <Badge bg="secondary">{i + 1}</Badge>
                      <span>{code}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="d-grid">
              <Button
                variant="primary"
                size="lg"
                onClick={onComplete}
                style={{ borderRadius: 8, fontWeight: 600 }}
              >
                {t("authenticatorSetupComplete")}
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default SetupFor2FA;
